import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { REV } from "../plate/SheetFurniture";
import SheetNote from "../SheetNote";
import {
  notes as seedNotes,
  fetchNotes,
  sortNotes,
  formatPostDate,
} from "../../data/notes.js";

const Paragraphs = ({ body }) => (
  <>
    {body.split(/\n\n+/).map((para, i) => (
      <p key={i} className="studio-note__para">
        {para}
      </p>
    ))}
  </>
);

const PostDate = ({ date }) => {
  const label = formatPostDate(date);
  if (!label) return null;
  return (
    <time className="studio-note__date" dateTime={date}>
      {label}
    </time>
  );
};

const PostImage = ({ note }) => {
  if (!note.image) return null;
  const className = note.imageCrop
    ? "studio-note__image studio-note__image--cropped"
    : "studio-note__image";
  return (
    <div className={className}>
      <img
        src={note.image}
        alt={note.title}
        style={note.imageCrop ? { objectPosition: note.imageCrop } : undefined}
      />
    </div>
  );
};

const PostLink = ({ note }) => {
  if (!note.link) return null;
  return (
    <a
      className="studio-note__link"
      href={note.link}
      target="_blank"
      rel="noopener noreferrer"
    >
      {note.linkLabel || "Read more"} →
    </a>
  );
};

const FeaturedNote = ({ note }) => (
  <article
    className={`studio-note studio-note--featured${
      note.image ? "" : " studio-note--no-image"
    }`}
  >
    <PostImage note={note} />
    <div className="studio-note__body">
      <PostDate date={note.date} />
      <h3 className="studio-note__title">{note.title}</h3>
      <Paragraphs body={note.body} />
      <PostLink note={note} />
    </div>
  </article>
);

const CollapsibleNote = ({ note, expanded, onToggle, index }) => (
  <motion.article
    className={`studio-note studio-note--collapsible ${
      expanded ? "is-expanded" : ""
    }`}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{
      duration: 0.5,
      delay: index * 0.06,
      ease: [0.22, 1, 0.36, 1],
    }}
  >
    <button
      className="studio-note__toggle"
      onClick={onToggle}
      aria-expanded={expanded}
    >
      <span className="studio-note__toggle-text">
        <h3 className="studio-note__title studio-note__title--small">
          {note.title}
        </h3>
        <PostDate date={note.date} />
      </span>
      <span className="studio-note__chevron" aria-hidden="true">
        {expanded ? "−" : "+"}
      </span>
    </button>

    {expanded && (
      <div
        className={`studio-note__expanded${
          note.image ? "" : " studio-note--no-image"
        }`}
      >
        <PostImage note={note} />
        <div className="studio-note__body">
          <Paragraphs body={note.body} />
          <PostLink note={note} />
        </div>
      </div>
    )}
  </motion.article>
);

// ---- Sheet N-1: the /notes page, drawn as an engineering plate ----
// The words are Liz's and pass through untouched; only the layout is the
// plate's. Each older note is a native <details>: keyboard operable and
// announced with no ARIA, and its text stays in the DOM, so find-in-page and
// screen readers reach every note whether or not it is open.
const ZONES_X = ["1", "2", "3", "4", "5", "6"];
const ZONES_Y = ["A", "B", "C", "D", "E", "F"];

const SheetBody = ({ note }) => (
  <div className={`notes-sheet__content${note.image ? "" : " notes-sheet__content--no-image"}`}>
    {note.image && (
      <div className="notes-sheet__image">
        <img
          src={note.image}
          alt={note.title}
          style={note.imageCrop ? { objectPosition: note.imageCrop } : undefined}
        />
      </div>
    )}
    <div className="notes-sheet__text">
      {note.body.split(/\n\n+/).map((para, i) => (
        <p key={i} className="notes-sheet__para">{para}</p>
      ))}
      {note.link && (
        <a className="notes-sheet__link" href={note.link} target="_blank" rel="noopener noreferrer">
          {note.linkLabel || "Read more"} →
        </a>
      )}
    </div>
  </div>
);

const SheetDate = ({ date }) => {
  const label = formatPostDate(date);
  if (!label) return null;
  return <time className="notes-sheet__date" dateTime={date}>{label}</time>;
};

const SheetEntry = ({ note, index }) => {
  const reduce = useReducedMotion();
  return (
    <motion.details
      className="notes-sheet__entry"
      initial={reduce ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
    >
      <summary className="notes-sheet__summary">
        <h2 className="notes-sheet__title">{note.title}</h2>
        <SheetDate date={note.date} />
      </summary>
      <SheetBody note={note} />
    </motion.details>
  );
};

export const NotesSheet = () => {
  const [list, setList] = useState(seedNotes);

  useEffect(() => {
    let cancelled = false;
    fetchNotes().then((data) => {
      if (!cancelled) setList(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const sorted = sortNotes(list);
  if (sorted.length === 0) return null;
  const [featured, ...rest] = sorted;

  return (
    <main className="notes-sheet" id="main">
      <div className="notes-sheet__zones notes-sheet__zones--x" aria-hidden="true">
        {ZONES_X.map((z) => <span key={z}>{z}</span>)}
      </div>
      <div className="notes-sheet__zones notes-sheet__zones--y" aria-hidden="true">
        {ZONES_Y.map((z) => <span key={z}>{z}</span>)}
      </div>

      <header className="notes-sheet__head">
        <p className="notes-sheet__eyebrow">FIELD NOTES</p>
        <h1 className="notes-sheet__name">Notes</h1>
      </header>

      <section id="notes" className="notes-sheet__list" aria-label="Notes">
        <article className="notes-sheet__featured">
          <header className="notes-sheet__featured-head">
            <h2 className="notes-sheet__title">{featured.title}</h2>
            <SheetDate date={featured.date} />
          </header>
          <SheetBody note={featured} />
        </article>
        {rest.map((note, idx) => (
          <SheetEntry key={note.id} note={note} index={idx} />
        ))}
      </section>

      <SheetNote />

      <section className="notes-sheet__titleblock" aria-label="Title block">
        <p className="notes-sheet__tb-name">LIZ OSBORN</p>
        <p className="notes-sheet__tb-sub">NOTES</p>
        <p className="notes-sheet__tb-cell">SHEET &nbsp;<b>N-1</b></p>
        <p className="notes-sheet__tb-cell">REV &nbsp;<b>{REV}</b></p>
      </section>
    </main>
  );
};

const Notes = () => {
  // Render the seed immediately, then replace with Supabase data on mount.
  const [list, setList] = useState(seedNotes);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    fetchNotes().then((data) => {
      if (!cancelled) setList(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const sorted = sortNotes(list);
  if (sorted.length === 0) return null;

  const [featured, ...rest] = sorted;

  return (
    <section id="notes" className="studio-notes">
      <div className="studio-notes__fade-out" aria-hidden="true" />
      <div className="studio-notes__inner">
        <h2 className="studio-notes__title">Notes</h2>

        <FeaturedNote note={featured} />

        {rest.length > 0 && (
          <div className="studio-notes__archive">
            {rest.map((note, idx) => (
              <CollapsibleNote
                key={note.id}
                note={note}
                index={idx}
                expanded={expandedIds.has(note.id)}
                onToggle={() => toggle(note.id)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Notes;
